import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCalendarEvent, IconChecklist } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Appointment, Organization, Task } from '@medplum/fhirtypes';
import { SearchInput } from '../../components/SearchInput';
import { LocalhostOnlyBadge } from '../../components/LocalhostOnlyBadge';
import { FEATURE_VISIBILITY } from '../../config/featureVisibility';
import { getTaskDisplayDescription } from '../../lib/portalAppointmentTaskDisplay';
import {
  buildProviderTaskNavigationUrl,
  resolveProviderTaskDestination,
} from '../../lib/providerTaskNavigation';
import { isFhirAppointmentUpcoming } from '../../lib/appointmentEffectiveEnd';
import { resolveCurrentUserPractitioner } from '../../lib/practitionerIdentity';
import { getPortalTiles, type PortalTileData } from '@unify/fhir-model/resources/organization';
import { normalizeExternalUrl } from '../../lib/normalizeExternalUrl';
import { useTasks } from '../tasks/hooks/useTasks';
import { getTaskDueDate, isTaskOverdue } from '../tasks/services/taskFhirMapping';

const TILE_IMAGE_URL = '/tile-image.png';

export interface HomeTileItem {
  title: string;
  href: string;
  imageUrl: string;
}

type HomeAppointmentCategory = 'session' | 'group' | 'meeting' | 'time-entry';

interface HomeAppointment {
  id: string;
  timeLabel: string;
  title: string;
  clientName: string;
  category: HomeAppointmentCategory;
  /** Display label for the badge (e.g. "Supervision", "Vacation"). */
  badgeLabel: string;
  joinUrl: string;
}

function getDashboardDueInfo(task: Task): { label: string; color: string } {
  const dueDate = getTaskDueDate(task);
  if (!dueDate) return { label: 'No due date', color: 'gray' };
  if (isTaskOverdue(task)) return { label: 'Overdue', color: 'red' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return { label: 'Today', color: 'yellow' };
  if (diffDays === 1) return { label: 'Tomorrow', color: 'blue' };
  if (diffDays <= 7) return { label: 'This week', color: 'blue' };
  return { label: due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: 'gray' };
}

function formatTimeLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = d.getMinutes();
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

const CATEGORY_SYSTEM = 'https://unify.health/fhir/appointment-category';
const VALID_CATEGORIES = new Set<string>(['session', 'group', 'meeting', 'time-entry']);

function resolveCategory(appt: Appointment): HomeAppointmentCategory {
  const codings = appt.serviceCategory?.flatMap((sc) => sc.coding ?? []) ?? [];
  const match = codings.find(
    (c) => c.system === CATEGORY_SYSTEM && c.code && VALID_CATEGORIES.has(c.code),
  );
  return (match?.code as HomeAppointmentCategory) ?? 'session';
}

function resolveServiceTypeLabel(appt: Appointment): string | undefined {
  const coding = appt.serviceType?.[0]?.coding?.[0];
  return coding?.display?.trim() || appt.serviceType?.[0]?.text?.trim() || undefined;
}

function appointmentToHomeAppointment(appt: Appointment): HomeAppointment | null {
  if (!appt.id || !appt.start) return null;
  const patientParticipant = (appt.participant ?? []).find((p) =>
    p.actor?.reference?.startsWith('Patient/'),
  );
  const practitionerParticipant = (appt.participant ?? []).find((p) =>
    p.actor?.reference?.startsWith('Practitioner/'),
  );
  const category = resolveCategory(appt);
  const serviceLabel = resolveServiceTypeLabel(appt);
  const timeLabel = formatTimeLabel(appt.start);

  let title: string;
  let badgeLabel: string;
  let clientName: string;

  switch (category) {
    case 'meeting':
      title = `${timeLabel} — ${serviceLabel || 'Meeting'}`;
      badgeLabel = serviceLabel || 'Meeting';
      clientName = practitionerParticipant?.actor?.display ?? 'Meeting';
      break;
    case 'time-entry':
      title = `${timeLabel} — ${serviceLabel || 'Time Entry'}`;
      badgeLabel = serviceLabel || 'Time Entry';
      clientName = serviceLabel || 'Time Entry';
      break;
    case 'group':
      title = `${timeLabel} — Group Session`;
      badgeLabel = serviceLabel || 'Group';
      clientName = patientParticipant?.actor?.display ?? 'Group Session';
      break;
    default:
      clientName = patientParticipant?.actor?.display ?? 'Session';
      title = `${timeLabel} with ${clientName}`;
      badgeLabel = serviceLabel || 'Session';
      break;
  }

  return {
    id: appt.id,
    timeLabel,
    title,
    clientName,
    category,
    badgeLabel,
    joinUrl: `/dashboard/session/${appt.id}`,
  };
}

function useTodayAppointments(): { appointments: HomeAppointment[]; loading: boolean } {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [appointments, setAppointments] = useState<HomeAppointment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchToday = useCallback(async () => {
    if (!profile) {
      setLoading(false);
      return;
    }
    try {
      const practitioner = await resolveCurrentUserPractitioner(medplum, profile);
      if (!practitioner?.id) {
        setAppointments([]);
        setLoading(false);
        return;
      }
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      const search = new URLSearchParams();
      search.set('actor', `Practitioner/${practitioner.id}`);
      search.append('date', `ge${startOfDay.toISOString()}`);
      search.append('date', `lt${endOfDay.toISOString()}`);
      search.set('status', 'proposed,booked,fulfilled,noshow');
      search.set('_sort', 'date');
      search.set('_count', '20');

      medplum.invalidateSearches('Appointment');
      const results = await medplum.searchResources('Appointment', search);

      const mapped = results
        .filter((appt) => isFhirAppointmentUpcoming(appt))
        .map(appointmentToHomeAppointment)
        .filter((a): a is HomeAppointment => a !== null);
      setAppointments(mapped);
    } catch {
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    void fetchToday();
  }, [fetchToday]);

  return { appointments, loading };
}

const ORG_EXT_URL = 'https://unify.health/fhir/organization';

function portalTilesToHomeTiles(tiles: PortalTileData[]): HomeTileItem[] {
  return [...tiles]
    .sort((a, b) => a.order - b.order)
    .map((t) => ({
      title: t.title,
      href: normalizeExternalUrl(t.link),
      imageUrl: t.imageUrl || TILE_IMAGE_URL,
    }));
}

function usePracticePortalTiles(): { tiles: HomeTileItem[]; loading: boolean } {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [tiles, setTiles] = useState<HomeTileItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!profile) {
          if (!cancelled) {
            setTiles([]);
            setLoading(false);
          }
          return;
        }
        const practitioner = await resolveCurrentUserPractitioner(medplum, profile);
        const orgRef = practitioner?.extension?.find((e) => e.url === ORG_EXT_URL)?.valueReference?.reference;
        const orgId = orgRef?.replace('Organization/', '');
        if (!orgId) {
          if (!cancelled) {
            setTiles([]);
            setLoading(false);
          }
          return;
        }
        const org: Organization = await medplum.readResource('Organization', orgId);
        const portalTiles = getPortalTiles(org);
        if (!cancelled) {
          setTiles(portalTiles?.length ? portalTilesToHomeTiles(portalTiles) : []);
        }
      } catch {
        if (!cancelled) setTiles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [medplum, profile]);

  return { tiles, loading };
}

/**
 * Dashboard page (/dashboard).
 * Grid of 140×140 tiles: logo image + title, each linking to an external resource. Hover adds shadow.
 */
export function HomeTilesPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [search, setSearch] = useState('');
  const { appointments: todayAppointments, loading: appointmentsLoading } = useTodayAppointments();
  const { tiles: practiceResourceTiles, loading: tilesLoading } = usePracticePortalTiles();

  // Real Medplum-backed tasks for the dashboard panel
  const { tasks: allTasks, loading: tasksLoading } = useTasks();

  const currentUserRef = useMemo<string | undefined>(() => {
    if (profile?.resourceType === 'Practitioner' && profile.id) {
      return `Practitioner/${profile.id}`;
    }
    return undefined;
  }, [profile]);

  // Show active tasks assigned to the current user (max 5 for the dashboard preview)
  const dashboardTasks = useMemo(() => {
    return allTasks
      .filter((t) =>
        t.status !== 'completed' &&
        t.status !== 'cancelled' &&
        (currentUserRef == null || t.owner?.reference === currentUserRef),
      )
      .slice(0, 5);
  }, [allTasks, currentUserRef]);

  const openTaskCount = dashboardTasks.length;

  const handleDashboardTaskClick = useCallback(async (task: Task): Promise<void> => {
    const destination = await resolveProviderTaskDestination(medplum, task);
    if (destination) {
      navigate(buildProviderTaskNavigationUrl(destination));
      return;
    }
    navigate('/dashboard/tasks');
  }, [medplum, navigate]);

  const filteredTiles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return practiceResourceTiles;
    return practiceResourceTiles.filter((t) => t.title.toLowerCase().includes(q));
  }, [search, practiceResourceTiles]);

  const handleJoinAppointment = (appointment: HomeAppointment): void => {
    window.open(appointment.joinUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Box>
          <Title order={2} mb="xs">
            Dashboard
          </Title>
          <Text size="sm" c="gray.6">
            Quick view of what needs attention today, plus frequently used resources.
          </Text>
        </Box>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex' }}>
            <Paper p="lg" withBorder style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <IconCalendarEvent size={18} stroke={1.5} />
                  <Title order={3}>Upcoming today</Title>
                </Group>
                <Badge variant="light" color="blue">
                  {appointmentsLoading ? '…' : `${todayAppointments.length} event${todayAppointments.length === 1 ? '' : 's'}`}
                </Badge>
              </Group>

              <Stack gap="sm" style={{ flex: 1 }}>
                {appointmentsLoading ? (
                  <Text size="sm" c="dimmed">Loading appointments...</Text>
                ) : todayAppointments.length === 0 ? (
                  <Text size="sm" c="dimmed">No appointments scheduled for today.</Text>
                ) : (
                  todayAppointments.map((appt) => {
                    const badgeColor =
                      appt.category === 'meeting' ? 'violet'
                        : appt.category === 'time-entry' ? 'gray'
                          : appt.category === 'group' ? 'grape'
                            : 'blue';
                    const isSession = appt.category === 'session' || appt.category === 'group';
                    return (
                      <Paper
                        key={appt.id}
                        withBorder
                        radius="md"
                        p="sm"
                        style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
                      >
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                          <Stack gap={2} style={{ minWidth: 0 }}>
                            <Text size="sm" fw={500}>
                              {appt.title}
                            </Text>
                            <Badge variant="light" color={badgeColor} size="sm">
                              {appt.badgeLabel}
                            </Badge>
                          </Stack>

                          <Stack gap={6} align="flex-end" style={{ flexShrink: 0 }}>
                            {isSession && (
                              <Button size="xs" color="gold" variant="light" onClick={() => handleJoinAppointment(appt)}>
                                Join Session
                              </Button>
                            )}
                            <Button size="xs" variant="subtle" onClick={() => navigate(`/dashboard/calendar?eventId=${appt.id}`)}>
                              {isSession ? 'Manage Session' : 'View on Calendar'}
                            </Button>
                          </Stack>
                        </Group>
                      </Paper>
                    );
                  })
                )}
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex' }}>
            <Paper p="lg" withBorder style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <IconChecklist size={18} stroke={1.5} />
                  <Title order={3}>Tasks to complete</Title>
                </Group>
                <Badge variant="light" color={openTaskCount > 0 ? 'blue' : 'green'}>
                  {openTaskCount} open
                </Badge>
              </Group>

              <Stack gap="xs" style={{ flex: 1 }}>
                {tasksLoading ? (
                  <Text size="sm" c="dimmed">Loading tasks...</Text>
                ) : dashboardTasks.length === 0 ? (
                  <Text size="sm" c="dimmed">No tasks to complete.</Text>
                ) : (
                  dashboardTasks.map((task) => {
                    const due = getDashboardDueInfo(task);
                    const title = getTaskDisplayDescription(task, 'provider') || 'Untitled task';
                    return (
                      <Card
                        key={task.id}
                        withBorder
                        padding="sm"
                        radius="md"
                        onClick={() => void handleDashboardTaskClick(task)}
                        className="dashboard-task-row"
                        style={{
                          cursor: 'pointer',
                          transition: 'box-shadow 150ms ease',
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                          <Text size="sm" fw={600} lineClamp={2} style={{ minWidth: 0 }}>
                            {title}
                          </Text>
                          <Badge size="sm" variant="light" color={due.color} style={{ flexShrink: 0 }}>
                            {due.label}
                          </Badge>
                        </Group>
                      </Card>
                    );
                  })
                )}
              </Stack>

              <style>{`.dashboard-task-row:hover { box-shadow: var(--mantine-shadow-sm); }`}</style>

              <Group justify="flex-end" mt="md">
                <Button size="xs" variant="light" onClick={() => navigate('/dashboard/tasks')}>
                  View all tasks
                </Button>
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>

        {FEATURE_VISIBILITY.dashboardPracticeResources ? (
        <Paper p="lg" withBorder>
          <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
            <Box>
              <Group gap="sm" align="center" wrap="wrap">
                <Title order={2} mb={0}>
                  Practice Resources
                </Title>
                <LocalhostOnlyBadge />
              </Group>
              <Text size="sm" c="gray.6">
                Frequently used links and tools.
              </Text>
            </Box>

            <SearchInput
              placeholder="Search links..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              maw={400}
            />
          </Group>

          <Divider my="md" />

          {tilesLoading ? (
            <Text size="sm" c="dimmed">Loading resources…</Text>
          ) : filteredTiles.length === 0 ? (
            <Text size="sm" c="dimmed">
              {search.trim() ? 'No practice resources match your search.' : 'No practice resources yet.'}
            </Text>
          ) : (
            <>
              <style>{`.home-tile-link-wrap:hover { box-shadow: var(--mantine-shadow-md); transform: scale(1.05); }`}</style>
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 140px))',
                  gap: 'var(--mantine-spacing-xl)',
                  justifyContent: 'center',
                  justifyItems: 'start',
                  width: '100%',
                }}
              >
                {filteredTiles.map((tile) => (
                  <Box
                    key={tile.href}
                    component="span"
                    className="home-tile-link-wrap"
                    style={{
                      display: 'inline-block',
                      borderRadius: 'var(--mantine-radius-md)',
                      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    }}
                  >
                    <Anchor
                      href={tile.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="never"
                      style={{ display: 'block', width: 'fit-content' }}
                    >
                      <Stack
                        align="center"
                        gap="0"
                        style={{
                          borderRadius: 'var(--mantine-radius-md)',
                          backgroundColor: 'var(--mantine-color-gray-0)',
                          border: '1px solid var(--mantine-color-gray-3)',
                        }}
                      >
                        <Box w={140} h={140}>
                          <img
                            src={tile.imageUrl || TILE_IMAGE_URL}
                            alt=""
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              padding: 4,
                              borderRadius: 'var(--mantine-radius-md)',
                              boxSizing: 'border-box',
                            }}
                          />
                        </Box>
                        <Text size="sm" fw={500} c="dark.7" ta="center" lineClamp={2} my="xs">
                          {tile.title}
                        </Text>
                      </Stack>
                    </Anchor>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Paper>
        ) : null}
      </Stack>
    </Container>
  );
}
